import React from 'react';
import { Box, ButtonBase, IconButton, Popover } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import { OptionList, type OptionItem } from './OptionList';

export interface FilterSelectProps {
  label: string;
  options: OptionItem[];
  value: string[];
  onChange: (next: string[]) => void;
  /** What an empty filter says on its right. */
  allLabel?: string;
  loading?: boolean;
  disabled?: boolean;
}

// Below this, "Google Sea… +2" stops being readable and the box shows just
// the count instead.
const COUNT_ONLY_BELOW = 88;

/**
 * One filter as a field box: the filter's name on the left, what it is set to
 * on the right — "Carrier  All", "Carrier  F&G", or "Channel  Google Sea… +2"
 * with a cross to clear.
 *
 * Picking values never changes the box's size, so the filters around it do
 * not move: the box is as wide as its name needs, the picked values fill
 * whatever room is left and end in "…". With several picked, the "+2" always
 * shows; where there is no room for any of the text, the box shows the count.
 *
 * It fills the width it is given, so a row of them lines up edge to edge. A
 * filter in use takes the brand tint. Opens an `OptionList`. Usually laid out
 * by `FilterBar`.
 */
export function FilterSelect({
  label,
  options,
  value,
  onChange,
  allLabel = 'All',
  loading,
  disabled,
}: FilterSelectProps) {
  const boxRef = React.useRef<HTMLDivElement | null>(null);
  const [anchor, setAnchor] = React.useState<HTMLElement | null>(null);
  const active = value.length > 0;
  const more = value.length - 1;
  const labelFor = (v: string) => options.find((o) => o.value === v)?.label ?? v;

  const trailing = (t: Theme) => ({
    width: t.ds.sizes.control.sm,
    height: t.ds.sizes.control.sm,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  });

  return (
    <>
      <Box
        ref={boxRef}
        sx={(t) => ({
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          minWidth: 0,
          height: t.ds.sizes.control.md,
          pr: 0.25,
          borderRadius: `${t.ds.radius.sm}px`,
          border: `1px solid ${active ? t.tokens.brand.primary : t.tokens.border.default}`,
          bgcolor: active ? t.tokens.brand.primarySoft : t.tokens.surface.raised,
          transition: `border-color ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}`,
          '&:hover': { borderColor: active ? t.tokens.brand.primary : t.tokens.border.strong },
        })}
      >
        <ButtonBase
          disabled={disabled}
          onClick={() => setAnchor(boxRef.current)}
          aria-haspopup="dialog"
          aria-expanded={Boolean(anchor)}
          aria-label={active ? `${label}: ${value.map(labelFor).join(', ')}` : `${label}: ${allLabel}`}
          sx={(t) => ({
            ...t.typography.body2,
            flex: 1,
            minWidth: 0,
            height: '100%',
            gap: 1,
            pl: 1.25,
            justifyContent: 'flex-start',
            textAlign: 'left',
            borderRadius: 'inherit',
            '&.Mui-focusVisible': { outline: `2px solid ${t.tokens.border.focus}`, outlineOffset: 1 },
          })}
        >
          <Box
            component="span"
            sx={(t) => ({
              flexShrink: 0,
              whiteSpace: 'nowrap',
              color: active ? t.tokens.text.accent : t.tokens.text.secondary,
              fontWeight: active ? 600 : 500,
            })}
          >
            {label}
          </Box>

          {/* The value slot. Size containment means its contents add nothing
              to the box's natural width — it only takes the room left after
              the name — so picking a long value cannot resize the filters.
              It also lets the slot switch to a bare count when it is narrow. */}
          <Box
            component="span"
            sx={(t) => ({
              flex: '1 1 0',
              minWidth: '4ch',
              display: 'flex',
              alignItems: 'center',
              justifyContent: active ? 'flex-start' : 'flex-end',
              gap: 0.5,
              containerType: 'inline-size',
              color: active ? t.tokens.text.primary : t.tokens.text.tertiary,
              fontWeight: active ? 600 : 400,
              whiteSpace: 'nowrap',
              '& .KitFilterSelect-count': { display: 'none' },
              [`@container (max-width: ${COUNT_ONLY_BELOW}px)`]: more > 0
                ? {
                    '& .KitFilterSelect-values': { display: 'none' },
                    '& .KitFilterSelect-count': { display: 'inline-flex' },
                  }
                : {},
            })}
          >
            {!active && allLabel}
            {active && (
              <>
                <Box
                  component="span"
                  className="KitFilterSelect-values"
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}
                >
                  <Box component="span" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {labelFor(value[0])}
                  </Box>
                  {more > 0 && (
                    <Box component="span" sx={(t) => ({ flexShrink: 0, color: t.tokens.text.accent })}>
                      +{more}
                    </Box>
                  )}
                </Box>
                <Box
                  component="span"
                  className="KitFilterSelect-count"
                  sx={(t) => ({
                    ...t.typography.caption,
                    alignItems: 'center',
                    px: 0.75,
                    borderRadius: `${t.ds.radius.full}px`,
                    bgcolor: t.tokens.brand.primary,
                    color: t.tokens.brand.onPrimary,
                    fontWeight: 600,
                    fontVariantNumeric: 'tabular-nums',
                  })}
                >
                  {value.length}
                </Box>
              </>
            )}
          </Box>

          {!active && (
            <Box component="span" sx={(t) => ({ ...trailing(t), color: t.tokens.text.tertiary })}>
              <ExpandMoreRounded fontSize="small" />
            </Box>
          )}
        </ButtonBase>

        {/* A picked filter keeps its arrow, so it still reads as a menu that
            opens — swapping the arrow for the clear button made a set filter
            look like a tag that could only be removed. Clear sits before it. */}
        {active && (
          <>
            <IconButton
              aria-label={`Clear ${label}`}
              onClick={() => onChange([])}
              disabled={disabled}
              sx={(t) => ({
                ...trailing(t),
                p: 0,
                ml: 0.5,
                color: t.tokens.text.accent,
                fontSize: t.typography.body1.fontSize,
              })}
            >
              <CloseRounded fontSize="inherit" />
            </IconButton>
            <ButtonBase
              tabIndex={-1}
              aria-hidden
              disabled={disabled}
              onClick={() => setAnchor(boxRef.current)}
              sx={(t) => ({
                ...trailing(t),
                borderRadius: `${t.ds.radius.sm}px`,
                color: t.tokens.text.accent,
              })}
            >
              <ExpandMoreRounded fontSize="small" />
            </ButtonBase>
          </>
        )}
      </Box>

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            'aria-label': `${label} filter`,
            sx: (t: Theme) => ({
              mt: 0.5,
              width: Math.max(260, anchor?.offsetWidth ?? 0),
              maxWidth: 'calc(100vw - 32px)',
              bgcolor: t.tokens.surface.overlay,
              border: `1px solid ${t.tokens.border.subtle}`,
              borderRadius: `${t.ds.radius.md}px`,
              boxShadow: t.tokens.shadow.md,
            }),
          },
        }}
      >
        <OptionList
          options={options}
          value={value}
          onChange={onChange}
          loading={loading}
          searchPlaceholder={`Search ${label.toLowerCase()}`}
          autoFocus
        />
      </Popover>
    </>
  );
}
